/**
 * Cómo se nombra y se pinta lo propio de una alarma: su estado, por qué se
 * resolvió y las duraciones de silencio. Las etiquetas de TIPO de alarma son
 * del catálogo (`alarmKindLabel` en `eventTypeLabels.ts`), no de aquí.
 */
import type { Tone } from '@shared/lib/tones';
import type { IconName } from '@shared';
import type {
  AlarmResolveReason,
  AlarmState,
  NotificationDeliveryKind,
  NotificationDeliveryStatus,
  SilenceDuration,
} from '@/services/api';

export interface AlarmStateMeta {
  label: string;
  tone: Tone;
  icon: IconName;
}

/** En el orden en que se recorren. */
export const ALARM_STATES: Readonly<Record<AlarmState, AlarmStateMeta>> = {
  open: { label: 'Abierta', tone: 'danger', icon: 'notification_important' },
  acknowledged: { label: 'Reconocida', tone: 'warning', icon: 'visibility' },
  silenced: { label: 'Silenciada', tone: 'neutral', icon: 'notifications_off' },
  resolved: { label: 'Resuelta', tone: 'success', icon: 'check_circle' },
};

export const ALARM_STATE_KEYS = Object.keys(ALARM_STATES) as AlarmState[];

export function alarmStateLabel(state: string | null | undefined): string {
  if (!state) return '';
  return (ALARM_STATES as Readonly<Record<string, AlarmStateMeta>>)[state]?.label ?? state;
}

export const RESOLVE_REASON_LABELS: Readonly<Record<AlarmResolveReason, string>> = {
  event: 'Por evento',
  cleared: 'La condición desapareció',
  timeout: 'Por tiempo',
  manual: 'A mano',
};

export function resolveReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '';
  return (RESOLVE_REASON_LABELS as Readonly<Record<string, string>>)[reason] ?? reason;
}

export const SILENCE_OPTIONS: readonly { value: SilenceDuration; label: string }[] = [
  { value: '1h', label: '1 hora' },
  { value: '1d', label: '1 día' },
  { value: '7d', label: '7 días' },
  { value: 'until', label: 'Hasta una fecha…' },
];

/** Tope del «Hasta una fecha…»: el backend rechaza más de 90 días. */
export const SILENCE_MAX_DAYS = 90;

/** Qué fue un aviso enviado (`notification_deliveries.kind`). */
export const DELIVERY_KIND_LABELS: Readonly<Record<NotificationDeliveryKind, string>> = {
  event: 'Evento',
  alarm_open: 'Alarma abierta',
  alarm_resolve: 'Alarma resuelta',
  digest: 'Resumen',
};

export function deliveryKindLabel(kind: string | null | undefined): string {
  if (!kind) return '';
  return (DELIVERY_KIND_LABELS as Readonly<Record<string, string>>)[kind] ?? kind;
}

/** Estado de un aviso, y la clave de `StatusBadge` que le da el tono
 *  (`skipped` va como neutro). */
export const DELIVERY_STATUS_LABELS: Readonly<Record<NotificationDeliveryStatus, string>> = {
  pending: 'Pendiente',
  sent: 'Enviado',
  failed: 'Fallido',
  skipped: 'Omitido',
};
export const DELIVERY_STATUS_BADGE: Readonly<Record<NotificationDeliveryStatus, string>> = {
  pending: 'pending',
  sent: 'active',
  failed: 'suspended',
  skipped: 'inactive',
};

/** Las gravedades que puede tener una alarma, de menos a más. */
export const ALARM_SEVERITIES = ['warning', 'error', 'critical'] as const;

export function fmtDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}
